import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { FragranceCard } from "@/components/fragrance-card";
import { getCharacter, characters, getProductsForCharacter, type Character } from "@/lib/fragrance-data";
import { getCharacterImageAlt } from "@/lib/image-alt";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

export const Route = createFileRoute("/character-archive/$slug")({
  head: ({ params }) => {
    const character = getCharacter(params.slug);
    const title = character
      ? `${character.name} — ${character.epithet} · House of Apocrypha`
      : "Character Archive · House of Apocrypha";
    const description = character ? character.tagline : "The House of Apocrypha character archive.";
    const path = `https://houseofapocrypha.com/character-archive/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        ...(character
          ? [
              { property: "og:image", content: character.image },
              { property: "og:image:alt", content: `${character.name} — ${character.epithet}` },
              { name: "twitter:image", content: character.image },
            ]
          : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: path },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "Character Archive", item: "https://houseofapocrypha.com/character-archive" },
              ...(character ? [{ "@type": "ListItem", position: 3, name: character.name, item: path }] : []),
            ],
          }),
        },
      ],
    };
  },
  loader: ({ params }): { character: Character } => {
    const character = getCharacter(params.slug);
    if (!character) throw notFound();
    return { character };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="text-3xl text-parchment">Character not found</h1>
        <p className="mt-4 text-lg text-muted-foreground">This page has faded from the archive.</p>
        <Link to="/" className="mt-8 inline-block eyebrow text-gold hover:text-gold-soft">
          Return home
        </Link>
      </div>
    </div>
  ),
  // No per-route errorComponent — let the root boundary handle errors so
  // transient chunk-load failures during navigation get silently retried.
  component: CharacterPage,
});

function CharacterPage() {
  const { character } = Route.useLoaderData() as { character: Character };
  const related = characters.filter((c) => c.slug !== character.slug).slice(0, 4);
  const featuredProduct = getProductsForCharacter(character.slug)[0];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <main>
        <nav className="mx-auto max-w-4xl px-6 pt-8 text-sm">
          <Link to="/" className="text-muted-foreground transition-colors hover:text-gold">
            Home
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <Link to="/character-archive" className="text-muted-foreground transition-colors hover:text-gold">
            Character Archive
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-parchment">{character.name}</span>
        </nav>

        <article className="mx-auto max-w-4xl px-6 py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <p className="eyebrow">Character Archive</p>
            <h1 className="mt-4 text-4xl text-parchment sm:text-6xl">{character.name}</h1>
            <p className="mt-3 font-serif text-2xl italic text-gold">{character.epithet}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-10 max-w-2xl overflow-hidden rounded-md border border-border bg-card"
          >
            <div className="aspect-[5/4] overflow-hidden">
              <ResponsiveImage
                src={character.image}
                alt={getCharacterImageAlt(character)}
                width={1024}
                height={819}
                sizes="(min-width: 1024px) 900px, 100vw"
                priority
                pictureClassName="contents"
                className="h-full w-full object-cover"
              />
            </div>
          </motion.div>

          <p className="mx-auto mt-10 max-w-2xl text-center font-serif text-2xl italic leading-relaxed text-gold/90">
            {character.tagline}
          </p>

          <div className="ornament my-10">
            <span>✶</span>
          </div>

          <div className="mx-auto max-w-2xl space-y-6 text-xl leading-relaxed text-foreground/85">
            {character.paragraphs.map((p: string, i: number) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <dl className="mx-auto mt-12 max-w-2xl divide-y divide-border rounded-md border border-border bg-card/40 px-6">
            {[
              { term: "Role", value: character.role },
              { term: "Smells like", value: character.smellsLike },
              { term: "Used in", value: character.usedIn.join(", ") },
              { term: "Pairs well with", value: character.pairsWith },
            ].map((row) => (
              <div key={row.term} className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr] sm:gap-6">
                <dt className="font-display text-xs uppercase tracking-[0.2em] text-gold">
                  {row.term}
                </dt>
                <dd className="text-lg text-foreground/85">{row.value}</dd>
              </div>
            ))}
          </dl>

          {featuredProduct && (
            <div className="mt-12 text-center">
              <Link
                to="/products/$slug"
                params={{ slug: featuredProduct.slug }}
                className="inline-block rounded-sm bg-gradient-to-r from-gold-soft to-gold px-8 py-4 font-display text-xs uppercase tracking-[0.24em] text-ink transition-opacity hover:opacity-90"
              >
                Discover {featuredProduct.name}
              </Link>
            </div>
          )}
        </article>

        <section className="border-t border-border py-20">
          <div className="mx-auto max-w-7xl px-6">
            <h2 className="text-center text-2xl text-parchment sm:text-3xl">More from the Archive</h2>
            <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((c, i) => (
                <FragranceCard key={c.slug} character={c} index={i} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
